import { buildDocumentPreviewHtml, getDocumentPreviewMode } from "./documentPreview";
import { Toaster } from "./common";

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function openUrlInNewTab(url, title, previewWindow) {
  if (!url) return false;

  if (previewWindow && !previewWindow.closed) {
    try {
      previewWindow.opener = null;
      previewWindow.location.href = url;
      if (title) previewWindow.document.title = title;
      return true;
    } catch {
      /* fall through */
    }
  }

  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (opened) {
    try {
      opened.opener = null;
      if (title) opened.document.title = title;
    } catch {
      /* ignore cross-origin */
    }
    return true;
  }

  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
}

function previewFileLabel(documentName) {
  return String(documentName || "document").trim() || "document";
}

function showPreviewDocument(previewWindow, page, documentName) {
  if (!previewWindow || previewWindow.closed) return false;
  const title = previewFileLabel(documentName);
  try {
    previewWindow.opener = null;
    previewWindow.document.open();
    previewWindow.document.write(page);
    previewWindow.document.close();
    previewWindow.document.title = title;
    const hash = `#${encodeURIComponent(title)}`;
    const nextUrl = `${window.location.pathname}${window.location.search}${hash}`;
    previewWindow.history.replaceState({ documentPreview: title }, title, nextUrl);
    return true;
  } catch {
    return false;
  }
}

function embedPreviewPage(blobUrl, documentType, documentName, isImage) {
  const title = escapeHtml(previewFileLabel(documentName));
  const src = escapeHtml(blobUrl);
  const type = escapeHtml(documentType || "");
  const body = isImage
    ? `<img src="${src}" alt="${title}" />`
    : `<embed src="${src}" type="${type || "application/pdf"}" />`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title><style>
    html, body { margin: 0; height: 100%; background: #525659; }
    embed, iframe, img { border: 0; width: 100%; height: 100%; object-fit: contain; background: #525659; }
  </style></head><body>${body}</body></html>`;
}

/**
 * Open a document preview in a new browser tab.
 * The address bar shows the document name instead of a blob id.
 */
/** Call from the click handler before awaiting the download API. */
export function openDocumentPreviewTab() {
  try {
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    return tab;
  } catch {
    return null;
  }
}

export async function openDocumentInNewTab(
  blobUrl,
  documentType,
  documentName,
  previewWindow = null,
) {
  if (!blobUrl) {
    Toaster.error("Could not open preview for this file.");
    return false;
  }

  const mode = getDocumentPreviewMode(documentType, documentName);
  const title = documentName || "Document preview";

  if (mode === "image" || mode === "pdf") {
    const page = embedPreviewPage(blobUrl, documentType, title, mode === "image");
    if (showPreviewDocument(previewWindow, page, title)) return true;
    if (openUrlInNewTab(blobUrl, title, previewWindow)) return true;
    if (previewWindow && !previewWindow.closed) previewWindow.close();
    Toaster.warning(
      "Could not open a new tab. Allow popups for this site, or use Download.",
    );
    return false;
  }

  if (mode === "spreadsheet" || mode === "word" || mode === "text") {
    try {
      const result = await buildDocumentPreviewHtml(
        blobUrl,
        documentType,
        documentName,
      );
      if (result.mode === "html" && result.html) {
        const page = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(
          title,
        )}</title><style>
          body { margin: 16px; color: #1a1a1a; }
          .doc-preview-html table { border-collapse: collapse; width: 100%; margin-bottom: 24px; }
          .doc-preview-html th, .doc-preview-html td { border: 1px solid #ddd; padding: 6px 8px; }
          .doc-preview-html th { background: #f5f5f5; }
          .doc-preview-sheet-title { margin: 12px 0 8px; font-size: 14px; }
          .doc-preview-text { white-space: pre-wrap; font-size: 13px; }
        </style></head><body>${result.html}</body></html>`;
        if (showPreviewDocument(previewWindow, page, title)) return true;
        const htmlBlob = new Blob([page], { type: "text/html;charset=utf-8" });
        const htmlUrl = URL.createObjectURL(htmlBlob);
        const ok = openUrlInNewTab(htmlUrl, title, previewWindow);
        window.setTimeout(() => URL.revokeObjectURL(htmlUrl), 120000);
        if (!ok) {
          Toaster.warning(
            "Could not open a new tab. Allow popups for this site, or use Download.",
          );
        }
        return ok;
      }
    } catch (err) {
      if (previewWindow && !previewWindow.closed) previewWindow.close();
      Toaster.error(err?.message || "Preview is not available for this file.");
      return false;
    }
  }

  if (openUrlInNewTab(blobUrl, title, previewWindow)) return true;
  Toaster.warning(
    "This file type cannot be previewed in the browser. Use Download instead.",
  );
  return false;
}
